import React, {FC, createContext, useCallback, useContext, useMemo} from 'react'
import {useQuery} from 'react-query'
import {Get} from '../modules/functions/get/Get'

export interface ProductListItem {
  id: number
  name: string
  description: string
}

export interface ActiveProduct {
  id: number
  name: string
  description: string
  display_name: string | null
  activated_at: string | null
}

export interface ProductsActive {
  [key: string]: ActiveProduct
}

export interface DemandProduct {
  id: number
  products: string
  trait: string
}

export interface DemandeProducts {
  [key: string]: DemandProduct
}

export interface ProductsApiResponse {
  products_list: ProductListItem[]
  products_active: ProductsActive
  demandeProducts: DemandeProducts
}

export interface ProductDisplay {
  id: number
  name: string
  description: string
  display_name: string | null
  activated_at: string | null
  isActive: boolean
  treaty?: number
}

export interface ProductsContextValue {
  products: ProductDisplay[]
  productsActive: ProductsActive
  demandeProducts: DemandeProducts
  productsList: ProductListItem[]
  isLoading: boolean
  isError: boolean
  refetch: () => void
  getProductByName: (name: string) => ProductDisplay | undefined
  getTreatyStatus: (name: string) => number | undefined
}

const ProductsContext = createContext<ProductsContextValue | undefined>(undefined)

export const useProducts = () => {
  const context = useContext(ProductsContext)
  if (!context) {
    throw new Error('useProducts must be used within ProductsProvider')
  }
  return context
}

const transformToDisplayProducts = (
  productsActive: ProductsActive,
  demandeProducts: DemandeProducts
): ProductDisplay[] => {
  if (!productsActive || typeof productsActive !== 'object') {
    console.warn('productsActive is null or undefined')
    return []
  }

  return Object.values(productsActive).map((product) => {
    const demand = demandeProducts && demandeProducts[product.name]

    return {
      id: product.id,
      name: product.name,
      description: product.description || '',
      display_name: product.display_name,
      activated_at: product.activated_at,
      isActive: !!product.activated_at,
      treaty: demand ? parseInt(demand.trait, 10) : undefined,
    }
  })
}

const endpoint = process.env.REACT_APP_GET_PRODUCTS || 'v2/list-products'

const fetchProducts = async (): Promise<ProductsApiResponse> => {
  try {
    const data = await Get<ProductsApiResponse>({endpoint})

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid API response format')
    }

    return {
      products_list: data.products_list || [],
      products_active: data.products_active || {},
      demandeProducts: data.demandeProducts || {},
    }
  } catch (error) {
    console.error('Error in fetchProducts:', error)
    throw error
  }
}

interface ProductsProviderProps {
  children: React.ReactNode
}

export const ProductsProvider: FC<ProductsProviderProps> = ({children}) => {
  const {data, isLoading, isError, refetch} = useQuery<ProductsApiResponse>(
    'products',
    fetchProducts,
    {
      onError: (error) => {
        console.error('Error fetching products:', error)
      },
      refetchOnWindowFocus: false,
      retry: 2,
    }
  )

  const productsActive = useMemo(() => data?.products_active || {}, [data])

  const demandeProducts = useMemo(() => data?.demandeProducts || {}, [data])

  const productsList = useMemo(() => data?.products_list || [], [data])

  const products = useMemo(
    () => transformToDisplayProducts(productsActive, demandeProducts),
    [productsActive, demandeProducts]
  )

  const getProductByName = useCallback(
    (name: string): ProductDisplay | undefined => {
      return products.find((product) => product.name.toLowerCase() === name.toLowerCase())
    },
    [products]
  )

  const getTreatyStatus = useCallback(
    (name: string): number | undefined => {
      const product = products.find((item) => item.name.toLowerCase() === name.toLowerCase())
      return product?.treaty
    },
    [products]
  )

  const contextValue = useMemo(
    () => ({
      products,
      productsActive,
      demandeProducts,
      productsList,
      isLoading,
      isError,
      refetch,
      getProductByName,
      getTreatyStatus,
    }),
    [
      products,
      productsActive,
      demandeProducts,
      productsList,
      isLoading,
      isError,
      refetch,
      getProductByName,
      getTreatyStatus,
    ]
  )

  return <ProductsContext.Provider value={contextValue}>{children}</ProductsContext.Provider>
}

export default ProductsProvider
